
import { Component, Vue, Watch } from "vue-property-decorator";
import Notify from "@/utils/notifications";
import * as AutomationsServices from "@/api/helpers/Automations";
import _ from "underscore";

export interface Data {
  CID: string;
  campaignName: string;
  affiliates: Affiliate[];
}

export interface Affiliate {
  AFID: string;
  rawClicks: number;
  rawConversions: number;
  invalidAdvertiserIds: number;
  invalidCountryCodes: number;
  invalidRows: number;
  validClicks: number;
  validConversions: number;
  testInvalidation: number;
}

@Component
export default class FileUploader extends Vue {
  fullscreenLoading = false;
  currentStep = 1;
  fileList: File[] = [];
  fileContents: Array<any> = [];
  analyzedData: Array<Data> = [];
  pendingValues: Record<string, Partial<Affiliate>> = {};

  handlefileList(file: any, fileList: Array<any>) {
    this.fileList = fileList;
    this.readFiles();
    this.currentStep = 2;
  }

  // Initialize pending values when expanding
  initPendingValues(affiliate: Affiliate, cid: string) {
    const key = this.getPendingKey(cid, affiliate.AFID);
    const currentPending = this.pendingValues[key] || {};

    // Merge existing pending values with current affiliate data
    this.$set(this.pendingValues, key, {
      ...affiliate,
      ...currentPending,
    });
  }

  // Generate unique key for each affiliate
  getPendingKey(cid: string, afid: string): string {
    return `${cid}-${afid}`;
  }

  // Save individual field changes
  saveChanges(cid: string, affiliate: Affiliate, field: keyof Affiliate) {
    const key = this.getPendingKey(cid, affiliate.AFID);
    const rawValue = this.pendingValues[key][field];
    const numericValue = Number(rawValue);

    if (!isNaN(numericValue) && numericValue >= 0) {
      // Update the actual data
      this.$set(affiliate, field, numericValue);

      // Update pending values with the new value
      this.$set(this.pendingValues[key], field, numericValue);

      // Force update to trigger template re-render
      this.$forceUpdate();
      // Update calculated fields
      this.updateCalculatedFields(affiliate);

      Notify.successful("Cambios guardados correctamente");
    } else {
      Notify.error("Valor inválido. Debe ser un número positivo");
      // Reset to previous valid value
      this.$set(this.pendingValues[key], field, affiliate[field]);
    }
  }

  private updateCalculatedFields(affiliate: Affiliate) {
    // Update calculated invalidRows
    affiliate.invalidRows =
      (affiliate.invalidAdvertiserIds || 0) +
      (affiliate.invalidCountryCodes || 0) +
      (affiliate.testInvalidation || 0);

    // Update valid conversions and clicks
    affiliate.validConversions = Math.max(
      0,
      affiliate.rawConversions - affiliate.invalidRows
    );

    affiliate.validClicks = Math.max(
      0,
      affiliate.rawClicks - affiliate.invalidRows
    );
  }

  readFiles() {
    this.fileContents = [];
    this.fileList.forEach((file: any) => {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        this.fileContents.push({
          name: file.name,
          content: event.target.result,
        });
      };
      reader.readAsText(file.raw);
    });
  }

  buildFormData(files: Array<any>) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append("files", file.raw);
    });
    return formData;
  }

  async sendFiles() {
    if (!this.fileContents.length) {
      Notify.error("No hay archivos para enviar.");
      return;
    }

    this.fullscreenLoading = true;
    AutomationsServices.affiliation(this.buildFormData(this.fileList))
      .then(res => {
        this.analyzedData = res.data.data;
        Notify.successful(res.data.message);
      })
      .catch(error => {
        Notify.error(error.response.data.message);
      })
      .finally(() => {
        this.fullscreenLoading = false;
      });
  }

  // Limpiar archivos cargados
  clearFiles() {
    this.fileList = [];
    this.fileContents = [];
    (this.$refs.upload as HTMLFormElement).clearFiles();
    this.currentStep = 1;
  }

  private hasPendingChanges(
    cid: string,
    afid: string,
    field: keyof Affiliate,
    currentValue: number
  ): boolean {
    const key = this.getPendingKey(cid, afid);
    const pendingValue = this.pendingValues[key]?.[field];

    return Number(pendingValue) !== Number(currentValue);
  }

  // Add this watcher to initialize when analyzedData changes
  @Watch("analyzedData", { immediate: true, deep: true })
  onAnalyzedDataChanged(newVal: Data[]) {
    newVal.forEach(campaign => {
      campaign.affiliates.forEach(affiliate => {
        this.initPendingValues(affiliate, campaign.CID);
      });
    });
  }

  uploadFiles() {
    this.fullscreenLoading = true;

    AutomationsServices.uploadAffiliation(this.analyzedData)
      .then(res => {
        console.log(res);
        Notify.successful(res.data.message);
      })
      .catch(error => {
        Notify.error(error);
      })
      .finally(() => {
        this.fullscreenLoading = false;
      });
  }

  logger() {
    console.log(this.analyzedData);
  }
}
